import React, { useState, useRef } from 'react';
import { Clock, Activity, Calendar, Zap, TrendingUp, Navigation, Trophy, BarChart2, RefreshCw, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import ActivityDetails from './ActivityDetails';
import { PoweredByStrava, ViewOnStravaButton } from './StravaAssets';

const API_URL = import.meta.env.VITE_API_URL || 
(window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : 'https://api.plataforma-deportiva.com/api');

const translateSport = (type) => {
  const map = {
      'Ride': 'Ciclismo',
      'VirtualRide': 'Rodillo',
      'Run': 'Carrera',
      'VirtualRun': 'Cinta',
      'WeightTraining': 'Fuerza',
      'Swim': 'Natación',
      'Workout': 'Entrenamiento',
      'Walk': 'Caminata',
      'Hike': 'Senderismo',
      'Yoga': 'Yoga',
      'Tennis': 'Pádel/Tenis',
      'RacquetSport': 'Pádel/Tenis',
      'Squash': 'Pádel/Tenis',
      'Soccer': 'Fútbol',
      'Crossfit': 'CrossFit',
      'Pilates': 'Pilates'
  };
  return map[type] || type;
};

const formatTime = (secs) => {
  if (!secs) return '00:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

const getPaceOrSpeed = (activity) => {
  if (!activity.average_speed || activity.average_speed <= 0) return null;
  const isCycling = (activity.sport_type || activity.type || '').toLowerCase().match(/ride|bike|ciclismo/);
  if (isCycling) {
    return `${(activity.average_speed * 3.6).toFixed(1)} km/h`;
  }
  const paceSecs = 1000 / activity.average_speed;
  const mins = Math.floor(paceSecs / 60);
  const secs = Math.floor(paceSecs % 60);
  if (mins > 50 || isNaN(mins)) return null;
  return `${mins}:${secs.toString().padStart(2, '0')}/km`;
};

const getIntensityColor = (sufferScore) => {
  if (!sufferScore) return 'var(--text-secondary)';
  if (sufferScore > 100) return '#ef4444'; // Red
  if (sufferScore > 50) return '#f59e0b'; // Orange
  return 'var(--health-green)'; // Green
};

const ActivityCard = ({ act, athleteId }) => {
  const [stravaDetails, setStravaDetails] = useState(null);
  const [loadingStravaDetails, setLoadingStravaDetails] = useState(false);
  const [showStravaDetails, setShowStravaDetails] = useState(false);
  const [errorStr, setErrorStr] = useState(null);

  const handleFetchStravaDetails = async () => {
    if (showStravaDetails) {
      setShowStravaDetails(false);
      return;
    }
    setShowStravaDetails(true);
    if (stravaDetails) return; // Cached
    
    setLoadingStravaDetails(true);
    try {
      const url = athleteId 
        ? `${API_URL}/activities/${act.id}/details?userId=${athleteId}`
        : `${API_URL}/activities/${act.id}/details`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setStravaDetails(data);
        setErrorStr(null);
      } else {
        const errData = await res.json();
        setErrorStr(`No se han podido cargar los datos detallados.`);
      }
    } catch (err) {
      console.error("Error obteniendo detalles de Strava:", err);
      setErrorStr(err.message);
    } finally {
      setLoadingStravaDetails(false);
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
      border: '1px solid var(--glass-border)',
      borderRadius: '12px',
      padding: '1.25rem',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
    }}>
      {/* Color accent line on top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: getIntensityColor(act.suffer_score) }} />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        <div>
          <p style={{ margin: '0 0 0.2rem 0', fontSize: '0.95rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
            {act.name}
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Calendar size={12} /> {new Date(act.start_date || act.startDate).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
          </p>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '0.3rem 0.6rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
          {translateSport(act.sport_type || act.type)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginTop: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Distancia</span>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--health-cyan)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Navigation size={14} /> {(act.distance / 1000).toFixed(2)} km
          </span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tiempo</span>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={14} /> {formatTime(act.moving_time)}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ritmo/Vel.</span>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TrendingUp size={14} /> {getPaceOrSpeed(act) || '--'}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Esfuerzo</span>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: getIntensityColor(act.suffer_score), display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Zap size={14} /> {act.suffer_score ? Math.round(act.suffer_score) : '--'}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%' }}>
        <button
          type="button"
          onClick={handleFetchStravaDetails}
          disabled={loadingStravaDetails}
          style={{
            flex: 1,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid var(--glass-border)',
            borderRadius: '8px',
            padding: '0.5rem',
            color: 'var(--text-primary)',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: loadingStravaDetails ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
            opacity: loadingStravaDetails ? 0.7 : 1
          }}
          onMouseOver={(e) => { if (!loadingStravaDetails) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
          onMouseOut={(e) => { if (!loadingStravaDetails) e.currentTarget.style.background = 'rgba(255,255,255,0.02)' }}
        >
          {loadingStravaDetails ? (
            <><RefreshCw size={14} className="animate-spin" /> Cargando...</>
          ) : showStravaDetails ? (
            <><BarChart2 size={14} /> Ocultar Zonas y Parciales</>
          ) : (
            <><BarChart2 size={14} /> Ver Zonas y Parciales</>
          )}
        </button>

        {(!act.source || act.source === 'strava') && (
          <ViewOnStravaButton activityId={act.id} compact={true} />
        )}
      </div>

      {showStravaDetails && errorStr && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', borderRadius: '8px', color: '#fca5a5', fontSize: '0.8rem' }}>
          Error: {errorStr}
        </div>
      )}

      {showStravaDetails && stravaDetails && (
        <div style={{ marginTop: '1rem' }}>
          <ActivityDetails details={stravaDetails} />
        </div>
      )}

    </div>
  );
};

const RecentActivities = ({ activities, athleteId, onUploadFit }) => {
  const scrollContainerRef = useRef(null);

  if (!activities || activities.length === 0) return null;

  // Coger hasta las 10 más recientes para poder deslizar
  const recentActivities = activities.slice(0, 10);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
            <Activity size={18} color="var(--health-cyan)" /> Últimas Sesiones
          </h3>
          <PoweredByStrava />
          {onUploadFit && (
            <button 
              onClick={onUploadFit}
              title="Importar archivo manual .FIT"
              style={{
                background: 'rgba(99, 102, 241, 0.12)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                color: '#a5b4fc',
                borderRadius: '16px',
                padding: '0.25rem 0.65rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.22)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.12)'}
            >
              <Upload size={12} />
              <span>Subir .FIT</span>
            </button>
          )}
        </div>
        
        {recentActivities.length > 3 && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={scrollLeft}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)',
                borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-primary)', cursor: 'pointer', transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              onClick={scrollRight}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)',
                borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-primary)', cursor: 'pointer', transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      <div 
        ref={scrollContainerRef}
        style={{ 
          display: 'flex', 
          overflowX: 'auto', 
          gap: '1rem', 
          paddingBottom: '0.5rem',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          // Hide scrollbar but keep functionality
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none' 
        }}
      >
        <style>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .recent-activity-card-wrapper {
            min-width: 280px;
            max-width: 320px;
            flex: 0 0 auto;
            scroll-snap-align: start;
          }
          @media (min-width: 1024px) {
            .recent-activity-card-wrapper {
              min-width: calc(33.333% - 0.67rem);
              max-width: none;
            }
          }
        `}</style>
        {recentActivities.map(act => (
          <div key={act.id} className="recent-activity-card-wrapper">
            <ActivityCard act={act} athleteId={athleteId} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentActivities;
